import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Gavel, ImageOff, Zap, Sparkles } from "lucide-react";
import { Countdown } from "@/components/countdown";
import { StatusBadge, resolveAuctionStatus } from "@/components/status-badge";
import { GradeBadge } from "@/components/grade-badge";
import { SellerRatingInline } from "@/components/seller-rating-inline";
import { SellerBadgeStack } from "@/components/seller-badge-stack";
import { formatGBP } from "@/lib/format";
import { useCurrency, CURRENCY_SYMBOL } from "@/lib/currency/use-currency";
import { isBuyNowAvailable } from "@/lib/auctions";
import { buildAuctionSlug } from "@/lib/auction-slug";
import { shortLanguageCode, labelForTcgGame, labelForRarity } from "@/lib/grading/companies";
import { rowCategory, subcategoryLabel, attributeFieldsFor } from "@/lib/marketplace/categories";
import { cn } from "@/lib/utils";
import { SmartImage } from "@/components/smart-image";
import { RegistryChip } from "@/components/registry/registry-chip";
import { buildAuctionAlt } from "@/lib/image-alt";
import { useTranslation } from "@/i18n";


export type AuctionCardData = {
  id: string;
  title: string;
  images: string[];
  end_time: string;
  current_bid_pence: number | null;
  starting_price_pence: number;
  bid_count: number;
  grading_company: string | null;
  grade: string | null;
  card_type?: string | null;
  product_type?: string | null;
  accessory_category?: string | null;
  brand?: string | null;
  product_line_set?: string | null;
  compatible_game?: string | null;
  card_set?: string | null;
  card_number?: string | null;
  card_language?: string | null;
  finish?: string | null;
  edition?: string | null;
  condition?: string | null;
  quantity?: number | null;
  custom_grading_company?: string | null;
  custom_grade?: string | null;
  seller_id?: string;
  buy_now_enabled?: boolean | null;
  buy_now_price_pence?: number | null;
  keep_buy_now_after_bid?: boolean | null;
  reservation_expires_at?: string | null;
  promoted_until?: string | null;
  // Trading Card professional attributes
  trading_card_game?: string | null;
  trading_card_game_other?: string | null;
  rarity?: string | null;
  rarity_other?: string | null;
  grading_status?: string | null;
  is_autographed?: boolean | null;
  is_serial_numbered?: boolean | null;
  serial_number?: string | null;
  // Verified Card Registry indicators
  grading_cert_number?: string | null;
  cert_verified?: boolean | null;
  cert_verification_status?: string | null;
  category?: string | null;
  subcategory?: string | null;
  attributes?: Record<string, unknown> | null;
};




export function AuctionCard({
  auction,
  isVerifiedSeller = false,
  sellerRating = null,
  sellerRatingCount = null,
}: {
  auction: AuctionCardData;
  isVerifiedSeller?: boolean;
  sellerRating?: number | null;
  sellerRatingCount?: number | null;
}) {
  const { t } = useTranslation("auctions");
  const { currency, isGBP, convert, locale } = useCurrency();
  const price = auction.current_bid_pence ?? auction.starting_price_pence;
  const hasBids = (auction.current_bid_pence ?? 0) > 0;
  const image = auction.images?.[0];
  const binAvailable = isBuyNowAvailable(auction);
  const area = rowCategory(auction);
  const isCard = area === "cards";
  const attrs = (auction.attributes ?? {}) as Record<string, unknown>;
  const attrBadges = isCard ? [] : [
    subcategoryLabel(area, auction.subcategory),
    ...attributeFieldsFor(area, auction.subcategory)
      .filter((f) => f.filter && typeof attrs[f.key] === "string")
      .map((f) => (f.key.endsWith("size") ? `${f.label}: ${attrs[f.key]}` : String(attrs[f.key]))),
  ].filter(Boolean).slice(0, 4);
  const isPromoted = !!auction.promoted_until && new Date(auction.promoted_until) > new Date();
  const statusKind = resolveAuctionStatus({
    status: "active",
    end_time: auction.end_time,
    reservation_expires_at: auction.reservation_expires_at,
  });

  // Flash the price briefly when a real bid arrives (existing data → animation only).
  const prevPriceRef = useRef(price);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (prevPriceRef.current !== price) {
      prevPriceRef.current = price;
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 900);
      return () => clearTimeout(t);
    }
  }, [price]);



  return (
    <Link
      to="/auction/$slug"
      params={{ slug: buildAuctionSlug(auction) }}
      className={`group block rounded-2xl overflow-hidden backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 ${
        isPromoted
          ? "border border-primary/50 bg-white/[0.06] shadow-[0_0_0_1px_rgba(139,92,246,0.35),0_20px_60px_-20px_rgba(139,92,246,0.45)]"
          : "border border-white/10 bg-white/[0.04] hover:border-primary/45 hover:shadow-[0_20px_50px_-20px_rgba(139,92,246,0.35)]"
      }`}
    >
      <div className="aspect-[3/4] bg-black/40 relative overflow-hidden">
        {image ? (
          <SmartImage
            src={image}
            alt={buildAuctionAlt(auction)}
            width={360}
            height={480}
            widths={[240, 360, 480, 720]}
            sizes="(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 300px"
            quality={72}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <ImageOff className="h-8 w-8" />
          </div>
        )}
        {isCard && (
          <div className="absolute top-2 left-2">
            <GradeBadge auction={auction} size="xs" className="bg-background/80" />
          </div>
        )}
        {isPromoted && (
          <div className="absolute top-9 left-2 inline-flex items-center gap-1 rounded-md bg-gold-gradient text-black px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider shadow-gold">
            <Sparkles className="h-3 w-3" aria-hidden /> {t("card.featured", "Featured")}
          </div>
        )}
        <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
          <div className="rounded-full border border-primary/40 bg-background/85 backdrop-blur px-2.5 py-0.5 text-xs font-semibold text-foreground shadow-[0_0_14px_rgba(139,92,246,0.35)]">
            <Countdown endTime={auction.end_time} />
          </div>
          {/* Only surface the pill when it adds information beyond the clock. */}
          {statusKind !== "live" && (
            <StatusBadge kind={statusKind} size="xs" className="bg-background/80 backdrop-blur" />
          )}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-medium line-clamp-2 min-h-[2.5rem] group-hover:text-gold transition-colors">
          {auction.title}
        </h3>
        {auction.grading_cert_number && auction.grading_company && (
          <div className="mt-1.5">
            <RegistryChip
              provider={auction.grading_company}
              certNumber={auction.grading_cert_number}
              verificationStatus={
                auction.cert_verified ? "verified" : (auction.cert_verification_status ?? null)
              }
            />
          </div>
        )}
        {!isCard && (auction.brand || attrBadges.length > 0) && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {[auction.brand, ...attrBadges].filter(Boolean).map((b, i) => (
              <span key={i} className={cn("rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-muted-foreground line-clamp-1", i === 0 && "border-primary/30 bg-primary/10 text-primary")}>{b as string}</span>
            ))}
          </div>
        )}
        {isCard && (auction.brand || auction.product_line_set || auction.compatible_game) && (
          <p className="mt-1 text-[11px] text-muted-foreground line-clamp-1">

            {[auction.brand, auction.product_line_set, auction.compatible_game].filter(Boolean).join(" · ")}
            {auction.quantity && auction.quantity > 1 ? ` · ${t("card.qty", "Qty {count}", { count: auction.quantity })}` : ""}
          </p>
        )}
        {isCard && (auction.trading_card_game || auction.card_language || auction.finish || auction.rarity || auction.edition || auction.card_set || auction.card_number) && (() => {
          const gameLabel = auction.trading_card_game === "other"
            ? (auction.trading_card_game_other || t("card.other", "Other"))
            : labelForTcgGame(auction.trading_card_game);
          const langCode = shortLanguageCode(auction.card_language);
          const rarityLabel = auction.rarity === "other"
            ? (auction.rarity_other || null)
            : labelForRarity(auction.rarity);
          const setBadge = (auction.card_set || auction.card_number)
            ? [auction.card_set, auction.card_number ? `#${auction.card_number}` : null].filter(Boolean).join(" ")
            : null;
          const badges: { key: string; content: ReactNode; className?: string }[] = [];
          if (gameLabel) badges.push({ key: "game", content: gameLabel, className: "border-gold/30 bg-gold/5 text-gold" });
          if (langCode) badges.push({ key: "lang", content: langCode });
          if (rarityLabel) badges.push({ key: "rarity", content: rarityLabel });
          if (setBadge) badges.push({ key: "set", content: setBadge });
          if (auction.finish) badges.push({ key: "finish", content: auction.finish });
          if (auction.edition && auction.edition !== "Unlimited") {
            badges.push({ key: "edition", content: auction.edition, className: "border-gold/30 bg-gold/5 text-gold" });
          }
          if (auction.is_autographed) badges.push({ key: "auto", content: t("card.autographed", "Autographed"), className: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300" });
          if (auction.is_serial_numbered && auction.serial_number) {
            badges.push({ key: "serial", content: `#${auction.serial_number}`, className: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300" });
          }
          const MAX = 4;
          const shown = badges.slice(0, MAX);
          const extra = badges.length - shown.length;
          return (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {shown.map((b) => (
                <span
                  key={b.key}
                  className={cn(
                    "rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-muted-foreground line-clamp-1",
                    b.className,
                  )}
                >
                  {b.content}
                </span>
              ))}
              {extra > 0 && (
                <span className="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  +{extra}
                </span>
              )}
            </div>
          );
        })()}

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          <SellerBadgeStack sellerId={auction.seller_id ?? null} compact size="sm" />
          <SellerRatingInline
            rating={sellerRating}
            count={sellerRatingCount}
            variant="compact"
          />
        </div>

        <div className="mt-3 flex items-end justify-between">
          <div>
            <p className="text-xs text-muted-foreground">{hasBids ? t("card.currentBid", "Current bid") : t("card.starting", "Starting")}</p>
            <p className={cn("font-display text-2xl text-gold-gradient", flash && "animate-bid-flash")}>{formatGBP(price)}</p>
            {!isGBP && (
              <p className="text-xs text-muted-foreground">
                ≈ {CURRENCY_SYMBOL[currency]}{Math.round(convert(price)).toLocaleString(locale)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Gavel className="h-3 w-3" /> {auction.bid_count}
          </div>
        </div>
        {binAvailable && auction.buy_now_price_pence && (
          <div className="mt-2 inline-flex items-center gap-1 rounded-md border border-gold/40 bg-gold/10 px-2 py-0.5 text-[11px] font-medium text-gold">
            <Zap className="h-3 w-3" aria-hidden /> {t("card.buyNow", "Buy It Now")} {formatGBP(auction.buy_now_price_pence)}
          </div>
        )}
      </div>

    </Link>
  );
}

